import * as React from 'react';
import { useEffect, useState, useRef, MutableRefObject } from 'react';

import { TransformWrapper as ZoomableContentWrapper, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import styled, { ThemeProvider } from 'styled-components';

import { SPACING, COLOR, THEME } from '../../packages/constants';
import { Button } from '../button';
import { PreviewContent } from './preview-content';
import { PreviewNavigationButtons } from './preview-navigation-buttons';
import { PreviewPagination } from './preview-pagination';

export type ThumbnailVariants = 'show' | 'available' | 'hide';

export interface PreviewProps {
    /**
     * Page data
     */
    pageData: PageDataProps[];
    /**
     * Scroll listener
     */
    scrollListener: (e: any) => void;
    /**
     * Scroll ref
     */
    scrollRef: React.RefObject<HTMLDivElement>;
    /**
     * Show navigation?
     */
    showNavigation?: boolean;
    /**
     * Scroll offset
     */
    scrollOffset?: number;
    /**
     * Fixes the size of the page during loading.
     */
    isFixedSize?: boolean;
    /**
     * Show fullscreen capability?
     */
    hasFullscreen?: boolean;
    /**
     * Don't show the invisible page at bottom
     */
    isSinglePage?: boolean;
    /**
     * Limit how wide a page is allowed to be
     */
    maxPageWidth?: number;
    isSustain?: boolean;
    reportTitle?: string;
    customNavigationBarContent?: React.ReactNode;
    thumbnailVariant?: ThumbnailVariants;
    triggerThumbnailLoad?: () => void;
}

interface PageWrapperProps {
    showNavigation?: boolean;
    hasFullscreen?: boolean;
    isFullscreen?: boolean;
    isSinglePage?: boolean;
    isPortrait?: boolean;
    hasFullscreenUseState?: boolean;
}

export interface PageDataProps {
    id: string;
    content: string;
    title: string;
    pageType?: string;
    additionalPageNumber?: string;
    imageSrc?: string;
    isLoading?: boolean;
    isAdditionalPage?: boolean;
    map?: (e: any) => React.ReactNode;
    thumbnailIsLoading?: boolean;
}

interface StyledPreviewInnerProps {
    isFullscreen?: boolean;
    showNavigation?: boolean;
    isSinglePage?: boolean;
}

const StyledPreview = styled.div<PageWrapperProps>`
    background-color: ${COLOR.MAIN.LIGHT_GREY};
    height: ${(props) => (props.isSinglePage ? 'auto' : '100%')};
`;

const StyledPreviewInner = styled.div<StyledPreviewInnerProps>`
    background-color: ${COLOR.MAIN.LIGHT_GREY};
    display: flex;
    height: ${(props) => (props.isSinglePage ? 'auto' : '100%')};
    position: relative;
    padding-left: ${(props) => (props.showNavigation && props.isFullscreen ? SPACING.MEDIUM : undefined)};
    width: 100%;
`;

const PageViewPort = styled.div<PageWrapperProps>`
    height: 100%;
    padding: ${(props) => (props.isSinglePage ? `0px 0px` : `${SPACING.MEDIUM} 0px 0px`)};
    width: ${(props) => (props.isSinglePage ? '100%' : 'calc(100% - 200px)')};
    overflow-y: hidden;
    overflow-x: auto;
    box-sizing: border-box;
`;

const ZoomTools = styled.div.attrs((props: { isSinglePage: boolean }) => props)`
    display: flex;
    position: absolute;
    z-index: 2;
    bottom: ${(props) => (props.isSinglePage ? '-5px' : '5px')}; // if there's space below, put it there
    padding: 6px 6px 6px 10px;
`;

const ZoomButton = styled(Button)`
    margin-right: 6px;
    font-size: 14px;
    padding: 6px;
    height: 22px;
`;

export const Preview = React.forwardRef<HTMLDivElement, PreviewProps>(
    (
        {
            pageData,
            showNavigation,
            scrollOffset = -55,
            scrollRef,
            scrollListener,
            isFixedSize,
            hasFullscreen = false,
            isSinglePage = false,
            maxPageWidth,
            reportTitle,
            customNavigationBarContent,
            isSustain,
            thumbnailVariant,
            triggerThumbnailLoad,
            ...props
        },
        ref,
    ) => {
        const [showNavigationToggle, setNavigationToggle] = useState(showNavigation);
        const [thumbnailToggle, setThumbnailToggle] = useState(thumbnailVariant === 'show');
        const [data, setData] = useState(pageData);
        const [isFullscreen, setFullscreen] = useState(false);
        const [hasFullscreenUseState, setHasFullscreen] = useState(hasFullscreen);
        const styledPreviewInnerRef = useRef() as MutableRefObject<HTMLDivElement>;
        const [isPortrait, setIsPortrait] = useState(false);
        const transformComponentRef = useRef<ReactZoomPanPinchRef | null>(null);

        const handleFullscreen = () => {
            // taken from https://davidwalsh.name/fullscreen
            // i cant believe this is still a thing...
            function launchIntoFullscreen(element: any) {
                if (element.requestFullscreen) {
                    element.requestFullscreen();
                } else if (element.mozRequestFullScreen) {
                    element.mozRequestFullScreen();
                } else if (element.webkitRequestFullscreen) {
                    element.webkitRequestFullscreen();
                } else if (element.msRequestFullscreen) {
                    element.msRequestFullscreen();
                }
            }

            function exitFullscreen() {
                if (document.exitFullscreen) {
                    document.exitFullscreen();
                    // @ts-ignore
                } else if (document.mozCancelFullScreen) {
                    // @ts-ignore
                    document.mozCancelFullScreen();
                    // @ts-ignore
                } else if (document.webkitExitFullscreen) {
                    // @ts-ignore
                    document.webkitExitFullscreen();
                }
            }

            if (!document.fullscreenElement) {
                launchIntoFullscreen(styledPreviewInnerRef.current);
            } else {
                exitFullscreen();
            }
        };

        const resetZoomLocation = () => {
            if (transformComponentRef.current) {
                transformComponentRef.current.resetTransform();
            }
        };

        const toggleThumbnails = () => {
            if (!thumbnailToggle && triggerThumbnailLoad) {
                triggerThumbnailLoad();
            }
            setThumbnailToggle(!thumbnailToggle);
        };

        useEffect(() => {
            setNavigationToggle(showNavigation);
            setHasFullscreen(hasFullscreen);
            setData(pageData);

            if (styledPreviewInnerRef.current) {
                // @ts-ignore
                styledPreviewInnerRef.current.addEventListener('fullscreenchange', () => {
                    if (document.fullscreenElement) {
                        setFullscreen(true);
                    } else {
                        setFullscreen(false);
                    }
                });
            }
        }, [showNavigation, hasFullscreen, pageData, styledPreviewInnerRef]);

        useEffect(() => {
            data.forEach((page) => {
                if (page.id === 'Performance') setIsPortrait(true);
            });
        });

        return (
            <StyledPreview ref={ref} {...props} isSinglePage={isSinglePage}>
                <PreviewNavigationButtons
                    showFullscreenButton={hasFullscreenUseState ?? false}
                    showNavigation={showNavigationToggle ?? false}
                    isSinglePage={isSinglePage ?? false}
                    showThumbnails={thumbnailToggle ?? false}
                    isFullscreen={isFullscreen ?? false}
                    onToggleThumbnails={toggleThumbnails}
                    onToggleNavigation={() => setNavigationToggle(!showNavigationToggle)}
                    onToggleFullscreen={handleFullscreen}
                    customNavigationBarContent={customNavigationBarContent}
                    reportTitle={reportTitle}
                    isSustain={isSustain}
                    thumbnailVariant={thumbnailVariant ?? 'show'}
                />
                <StyledPreviewInner
                    isFullscreen={isFullscreen}
                    showNavigation={showNavigation}
                    ref={styledPreviewInnerRef}
                    isSinglePage={isSinglePage}
                >
                    {showNavigationToggle && (
                        <PreviewPagination
                            hasFullscreenOption={hasFullscreenUseState ?? false}
                            isFullscreen={isFullscreen}
                            pageData={data}
                            showThumbnails={thumbnailToggle}
                            scrollOffset={scrollOffset}
                            onPageSelect={resetZoomLocation}
                        />
                    )}
                    <PageViewPort isSinglePage={isSinglePage}>
                        <ZoomableContentWrapper
                            wheel={{ wheelDisabled: true }}
                            initialScale={1}
                            initialPositionX={0}
                            minScale={0.5}
                            ref={transformComponentRef}
                        >
                            {({ zoomIn, zoomOut, resetTransform }) => (
                                <>
                                    <PreviewContent
                                        showNavigation={showNavigationToggle ?? false}
                                        hasFullscreen={hasFullscreenUseState ?? false}
                                        isFullscreen={isFullscreen}
                                        isSinglePage={isSinglePage ?? false}
                                        isFixedSize={isFixedSize ?? false}
                                        isPortrait={isPortrait}
                                        maxPageWidth={maxPageWidth}
                                        scrollRef={scrollRef}
                                        scrollListener={scrollListener}
                                        pageData={pageData}
                                    />
                                    <ZoomTools isSinglePage={isSinglePage}>
                                        <ThemeProvider theme={THEME.GENERIC}>
                                            <ZoomButton dataId={'button__zoom-out'} onClick={() => zoomOut(0.25)}>
                                                {'\u2212'}
                                            </ZoomButton>
                                            <ZoomButton dataId={'button__zoom-in'} onClick={() => zoomIn(0.25)}>
                                                {'\u002B'}
                                            </ZoomButton>
                                            <ZoomButton dataId={'button__zoom-reset'} onClick={() => resetTransform()}>
                                                RESET
                                            </ZoomButton>
                                        </ThemeProvider>
                                    </ZoomTools>
                                </>
                            )}
                        </ZoomableContentWrapper>
                    </PageViewPort>
                </StyledPreviewInner>
            </StyledPreview>
        );
    },
);
Preview.displayName = 'Preview';
